import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { fetchConfig } from '@theme/utilities';

const CEP_STORAGE_KEY = 'safetti_shipping_cep';

/**
 * @typedef {Object} ViaCepAddress
 * @property {string} cep
 * @property {string} localidade
 * @property {string} uf
 * @property {boolean} [erro]
 */

/**
 * @typedef {Object} ShippingRate
 * @property {string} name
 * @property {string} presentment_name
 * @property {string} price
 */

/**
 * Brazilian CEP shipping calculator for PDP and cart drawer.
 *
 * @extends {Component}
 */
class ShippingCalculator extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#restoreCep();
    this.refs.cepInput?.addEventListener('input', this.#handleCepInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
    this.refs.cepInput?.removeEventListener('input', this.#handleCepInput);
  }

  /**
   * @param {InputEvent} event
   */
  #handleCepInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.target.value = this.#formatCep(event.target.value);
  };

  /**
   * @param {SubmitEvent} event
   */
  calculate = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { cepInput, loading, error, ratesList, ratesContainer, submitButton } = this.refs;
    if (!(cepInput instanceof HTMLInputElement)) return;

    const digits = this.#normalizeCep(cepInput.value);
    if (digits.length !== 8) {
      this.#showError(Theme.translations.shipping_calculator_invalid_cep);
      return;
    }

    const abortController = new AbortController();
    this.#activeFetch?.abort();
    this.#activeFetch = abortController;

    this.#setLoading(true);
    this.#hideError();
    this.#hideRates();

    try {
      const address = await this.#fetchAddress(digits, abortController.signal);
      if (!address) {
        this.#showError(Theme.translations.shipping_calculator_cep_not_found);
        return;
      }

      await this.#ensureCartHasItems(abortController.signal);
      await this.#updateShippingAddress(address, abortController.signal);

      const rates = await this.#fetchShippingRates(address, abortController.signal);
      if (!rates.length) {
        this.#showError(Theme.translations.shipping_calculator_no_rates);
        return;
      }

      sessionStorage.setItem(CEP_STORAGE_KEY, this.#formatCep(digits));
      this.#renderRates(rates, address);

      if (ratesContainer instanceof HTMLElement) {
        ratesContainer.classList.remove('hidden');
      }
    } catch (requestError) {
      if (requestError instanceof DOMException && requestError.name === 'AbortError') return;

      if (requestError instanceof Error) {
        if (requestError.message === 'empty-cart') {
          this.#showError(Theme.translations.shipping_calculator_empty_cart);
          return;
        }
        if (requestError.message === 'missing-variant') {
          this.#showError(Theme.translations.shipping_calculator_missing_variant);
          return;
        }
      }

      this.#showError(Theme.translations.shipping_calculator_error);
    } finally {
      this.#setLoading(false);
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
      }
      this.#activeFetch = null;
    }
  };

  #restoreCep() {
    const { cepInput } = this.refs;
    if (!(cepInput instanceof HTMLInputElement)) return;

    const saved = sessionStorage.getItem(CEP_STORAGE_KEY);
    if (saved) {
      cepInput.value = saved;
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const { loading, submitButton } = this.refs;
    loading?.classList.toggle('hidden', !isLoading);
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = isLoading;
    }
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { error } = this.refs;
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.classList.remove('hidden');
  }

  #hideError() {
    this.refs.error?.classList.add('hidden');
  }

  #hideRates() {
    this.refs.ratesContainer?.classList.add('hidden');
    if (this.refs.ratesList instanceof HTMLElement) {
      this.refs.ratesList.replaceChildren();
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeCep(value) {
    return String(value ?? '').replace(/\D/g, '').slice(0, 8);
  }

  /**
   * @param {string} digits
   * @returns {string}
   */
  #formatCep(digits) {
    const normalized = this.#normalizeCep(digits);
    if (normalized.length <= 5) return normalized;
    return `${normalized.slice(0, 5)}-${normalized.slice(5)}`;
  }

  /**
   * @param {string} digits
   * @param {AbortSignal} signal
   * @returns {Promise<ViaCepAddress | null>}
   */
  async #fetchAddress(digits, signal) {
    const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`, { signal });
    if (!response.ok) return null;

    const data = await response.json();
    if (!data || data.erro || !data.uf || !data.localidade) return null;

    return {
      ...data,
      cep: data.cep || digits,
    };
  }

  /**
   * @param {AbortSignal} signal
   */
  async #ensureCartHasItems(signal) {
    const cartResponse = await fetch('/cart.js', { signal });
    const cart = await cartResponse.json();

    if (cart.item_count > 0) return;

    if (this.dataset.context !== 'pdp') {
      throw new Error('empty-cart');
    }

    const variantId = this.#getSelectedVariantId();
    const quantity = this.#getSelectedQuantity();

    if (!variantId) {
      throw new Error('missing-variant');
    }

    const addResponse = await fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity }],
        }),
      }),
      signal,
    });

    const addData = await addResponse.json();
    if (!addResponse.ok) {
      throw new Error('add-failed');
    }

    document.dispatchEvent(
      new CartUpdateEvent(addData, this.id, {
        source: 'shipping-calculator',
        itemCount: addData.item_count,
        variantId: String(variantId),
      }),
    );
  }

  /**
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const scope = this.closest('.product-details') ?? document;
    const variantInput = scope.querySelector('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      return variantInput.value;
    }

    return this.dataset.variantId || null;
  }

  /**
   * @returns {number}
   */
  #getSelectedQuantity() {
    const scope = this.closest('.product-details') ?? document;
    const quantityInput = scope.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) {
      const quantity = Number.parseInt(quantityInput.value, 10);
      if (!Number.isNaN(quantity) && quantity > 0) return quantity;
    }

    return 1;
  }

  /**
   * @param {ViaCepAddress} address
   * @param {AbortSignal} signal
   */
  async #updateShippingAddress(address, signal) {
    const shippingAddress = {
      zip: this.#formatCep(address.cep || ''),
      country: 'Brazil',
      province: address.uf,
      city: address.localidade,
    };

    const response = await fetch(Theme.routes.cart_update_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({ shipping_address: shippingAddress }),
      }),
      signal,
    });

    if (!response.ok) {
      throw new Error('address-update-failed');
    }
  }

  /**
   * @param {ViaCepAddress} address
   * @param {AbortSignal} signal
   * @returns {Promise<ShippingRate[]>}
   */
  async #fetchShippingRates(address, signal) {
    const params = new URLSearchParams({
      'shipping_address[zip]': this.#formatCep(address.cep || ''),
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': address.uf,
      'shipping_address[city]': address.localidade,
    });

    const directResponse = await fetch(`/cart/shipping_rates.json?${params.toString()}`, { signal });
    if (directResponse.ok) {
      const directData = await directResponse.json();
      if (directData?.shipping_rates?.length) {
        return directData.shipping_rates;
      }
    }

    await fetch('/cart/prepare_shipping_rates.json', {
      ...fetchConfig('json', {
        body: JSON.stringify({
          shipping_address: {
            zip: this.#formatCep(address.cep || ''),
            country: 'Brazil',
            province: address.uf,
            city: address.localidade,
          },
        }),
      }),
      signal,
    });

    for (let attempt = 0; attempt < 8; attempt += 1) {
      await new Promise((resolve) => {
        window.setTimeout(resolve, 400);
      });

      const asyncResponse = await fetch('/cart/async_shipping_rates.json', { signal });
      if (!asyncResponse.ok) continue;

      const asyncData = await asyncResponse.json();
      if (asyncData?.shipping_rates?.length) {
        return asyncData.shipping_rates;
      }
    }

    return [];
  }

  /**
   * @param {ShippingRate[]} rates
   * @param {ViaCepAddress} address
   */
  #renderRates(rates, address) {
    const { ratesList } = this.refs;
    if (!(ratesList instanceof HTMLElement)) return;

    const moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';
    const currency = this.dataset.currency || 'BRL';
    const locationLabel = Theme.translations.shipping_calculator_rates_for
      .replace('[city]', address.localidade)
      .replace('[uf]', address.uf);

    ratesList.replaceChildren();

    const heading = document.createElement('p');
    heading.className = 'shipping-calculator__rates-heading';
    heading.textContent = locationLabel;
    ratesList.append(heading);

    const list = document.createElement('ul');
    list.className = 'shipping-calculator__rates';

    for (const rate of rates) {
      const item = document.createElement('li');
      item.className = 'shipping-calculator__rate';

      const name = document.createElement('span');
      name.className = 'shipping-calculator__rate-name';
      name.textContent = rate.presentment_name || rate.name;

      const price = document.createElement('span');
      price.className = 'shipping-calculator__rate-price';
      const priceMinorUnits = convertMoneyToMinorUnits(String(rate.price ?? ''), currency) ?? 0;
      price.textContent =
        priceMinorUnits === 0
          ? Theme.translations.shipping_calculator_free
          : formatMoney(priceMinorUnits, moneyFormat, currency);

      item.append(name, price);
      list.append(item);
    }

    ratesList.append(list);

    if (this.dataset.context === 'pdp') {
      const note = document.createElement('p');
      note.className = 'shipping-calculator__note';
      note.textContent = Theme.translations.shipping_calculator_pdp_note;
      ratesList.append(note);
    }
  }
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}
